import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import TradeShareModal from "@/components/TradeShareModal";
import { Share2, ChevronUp, ChevronDown, StickyNote, X, Check } from "lucide-react";

function fmt(n, d = 2) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}
function timeLeft(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "0:00";
  const s = Math.ceil(ms / 1000);  // ceil: 0:00 only when ms hits exactly 0
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2,"0")}:${String(r).padStart(2,"0")}` : `${m}:${String(r).padStart(2,"0")}`;
}

export default function PositionsPanel({ openTrades, history, tickers, onClosed, assetsMap, expanded, onToggle }) {
  const [tab, setTab] = useState("open");
  const [shareTrade, setShareTrade] = useState(null);
  const [editingNote, setEditingNote] = useState(null); // trade id
  const [noteText, setNoteText] = useState("");
  const [noteTags, setNoteTags] = useState("");
  const { t } = useI18n();
  const { user, refreshUser } = useAuth();
  const [, force] = useState(0);
  React.useEffect(() => { const i = setInterval(() => force((v) => v + 1), 1000); return () => clearInterval(i); }, []);

  const filteredByMode = (arr) => (arr || []).filter((t) => !user?.account_mode || t.mode === user.account_mode);
  const rows = tab === "open" ? filteredByMode(openTrades) : filteredByMode(history);

  const closeCfd = async (id) => {
    try {
      await api.post(`/trades/cfd/close/${id}`);
      toast.success("Position closed");
      await refreshUser(); onClosed?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const startEditNote = (trade) => {
    setEditingNote(trade.id);
    setNoteText(trade.note || "");
    setNoteTags((trade.tags || []).join(", "));
  };

  const saveNote = async (tradeId) => {
    try {
      const tags = noteTags.split(",").map(s => s.trim()).filter(Boolean);
      await api.post(`/trades/${tradeId}/note`, { note: noteText, tags });
      toast.success("Not kaydedildi.");
      setEditingNote(null);
      onClosed?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  // Calculate total investment and P&L for open trades (filtered by mode)
  const totalInvestment = rows.reduce((sum, t) => sum + Number(t.amount), 0);
  let totalPnl = 0;
  rows.forEach((r) => {
    const currentPrice = tickers[r.symbol]?.price;
    if (currentPrice != null) {
      const asset = assetsMap?.[r.symbol];
      if (r.type === "cfd") {
        const ret = (currentPrice - r.entry_price) / r.entry_price;
        const signed = r.direction === "sell" ? -ret : ret;
        let pnl = signed * r.amount * (r.leverage || 1);
        if (pnl < -r.amount) pnl = -r.amount;
        totalPnl += pnl;
      } else {
        const win = (r.direction === "call" && currentPrice > r.entry_price) || (r.direction === "put" && currentPrice < r.entry_price);
        totalPnl += win ? r.amount * (asset ? asset.payout / 100 : 0.8) : -r.amount;
      }
    }
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", background: "var(--iq-sidebar)" }}>
      {/* Collapsed bottom bar */}
      <div
        className="iq-bottom-bar"
        onClick={() => onToggle?.()}
        data-testid="positions-toggle"
        style={{ height: 36, background: expanded ? "var(--iq-panel-hover)" : "var(--iq-sidebar)" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <span style={{ fontWeight: 700, color: "#fff", fontSize: 12 }}>
            {rows.length > 0 ? `${t("positions")} (${rows.length})` : t("portfolio")}
          </span>
          {openTrades.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{
                  color: totalPnl >= 0 ? "var(--iq-green)" : "var(--iq-red)",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 13, fontWeight: 800,
                  textShadow: totalPnl >= 0 ? "0 0 8px rgba(0,230,118,0.5)" : "0 0 8px rgba(255,23,68,0.5)",
                }}>
                  {totalPnl >= 0 ? "+" : ""}${fmt(totalPnl)}
                </span>
                <span style={{
                  fontSize: 9, color: totalPnl >= 0 ? "var(--iq-green)" : "var(--iq-red)",
                  background: totalPnl >= 0 ? "var(--iq-green-dim)" : "var(--iq-red-dim)",
                  padding: "1px 5px", borderRadius: 2, fontWeight: 600,
                }}>P/L</span>
              </div>
              <span style={{ color: "var(--iq-text-dim)", fontSize: 11 }}>
                Yatırım <span style={{ color: "var(--iq-text-bright)" }}>${fmt(totalInvestment)}</span>
              </span>
            </>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--iq-text-dim)", fontSize: 11 }}>
          <span>{expanded ? "Gizle" : "Göster"}</span>
          {expanded ? <ChevronDown style={{ width: 14, height: 14 }} /> : <ChevronUp style={{ width: 14, height: 14 }} />}
        </div>
      </div>

      {/* Expanded positions table */}
      {expanded && (
        <div style={{ maxHeight: "220px", overflow: "hidden", display: "flex", flexDirection: "column", borderTop: "1px solid var(--iq-border)" }}>
          {/* Tabs */}
          <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid var(--iq-border)", padding: "0 12px", background: "var(--iq-panel)" }}>
            {["open", "history"].map((k) => (
              <button
                key={k}
                data-testid={`tab-${k}`}
                onClick={() => setTab(k)}
                style={{
                  padding: "8px 16px",
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  fontWeight: 600,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: tab === k ? "#fff" : "var(--iq-text-dim)",
                  borderBottom: tab === k ? "2px solid var(--iq-green)" : "2px solid transparent",
                  transition: "all 0.2s",
                }}
              >
                {k === "open" ? `${t("open_positions")} (${filteredByMode(openTrades).length})` : t("history")}
              </button>
            ))}
          </div>
          {/* Table */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            <table style={{ width: "100%", fontSize: "11px", fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
              <thead style={{ background: "var(--iq-bg)", position: "sticky", top: 0 }}>
                <tr style={{ color: "var(--iq-text-dim)", textTransform: "uppercase", fontSize: "9px", letterSpacing: "0.1em" }}>
                  <th style={{ textAlign: "left", padding: "6px 12px" }}>Asset</th>
                  <th style={{ textAlign: "left", padding: "6px 8px" }}>Type</th>
                  <th style={{ textAlign: "left", padding: "6px 8px" }}>Dir</th>
                  <th style={{ textAlign: "right", padding: "6px 8px" }}>Amount</th>
                  <th style={{ textAlign: "right", padding: "6px 8px" }}>Entry</th>
                  <th style={{ textAlign: "right", padding: "6px 8px" }}>{tab === "open" ? "Current" : "Close"}</th>
                  <th style={{ textAlign: "right", padding: "6px 8px" }}>P/L</th>
                  <th style={{ textAlign: "right", padding: "6px 12px" }}>{tab === "open" ? "Expiry" : "Status"}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: "16px", color: "var(--iq-text-dim)" }}>No positions</td></tr>
                )}
                {rows.map((r) => {
                  const asset = assetsMap?.[r.symbol];
                  const digits = asset?.digits ?? 2;
                  const currentPrice = tickers[r.symbol]?.price;
                  let pnl = r.pnl;
                  if (tab === "open" && currentPrice != null) {
                    if (r.type === "cfd") {
                      const ret = (currentPrice - r.entry_price) / r.entry_price;
                      const signed = r.direction === "sell" ? -ret : ret;
                      pnl = signed * r.amount * (r.leverage || 1);
                      if (pnl < -r.amount) pnl = -r.amount;
                    } else {
                      const win = (r.direction === "call" && currentPrice > r.entry_price) || (r.direction === "put" && currentPrice < r.entry_price);
                      pnl = win ? r.amount * (asset ? asset.payout / 100 : 0.8) : -r.amount;
                    }
                  }
                  const dirColor = r.direction === "call" || r.direction === "buy" ? "var(--iq-green)" : "#ef4444";
                  return (
                    <React.Fragment key={r.id}>
                    <tr
                      data-testid={`trade-row-${r.id}`}
                      style={{ borderBottom: "1px solid rgba(42,46,62,0.3)", cursor: "default" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "var(--iq-panel-hover)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={{ padding: "6px 12px" }}>{asset?.name || r.symbol}</td>
                      <td style={{ padding: "6px 8px", color: "var(--iq-text-dim)", textTransform: "uppercase" }}>{r.type}</td>
                      <td style={{ padding: "6px 8px", color: dirColor, textTransform: "uppercase" }}>{r.direction}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>${fmt(r.amount)}{r.type === "cfd" ? ` · ${r.leverage}x` : ""}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{fmt(r.entry_price, digits)}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{tab === "open" ? (currentPrice != null ? fmt(currentPrice, digits) : "—") : fmt(r.close_price, digits)}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", color: pnl >= 0 ? "var(--iq-green)" : "#ef4444" }}>
                        {pnl != null ? (pnl >= 0 ? "+" : "") + fmt(pnl) : "—"}
                      </td>
                      <td style={{ padding: "6px 12px", textAlign: "right" }}>
                        {tab === "open" && r.type === "binary" && <span style={{ color: "var(--iq-text-dim)" }}>{timeLeft(r.expires_at)}</span>}
                        {tab === "open" && r.type === "cfd" && (
                          <button
                            data-testid={`close-trade-${r.id}`}
                            onClick={() => closeCfd(r.id)}
                            style={{
                              padding: "3px 8px", fontSize: "9px", textTransform: "uppercase",
                              letterSpacing: "0.05em", background: "var(--iq-red-btn)", color: "#fff",
                              border: "none", borderRadius: "2px", cursor: "pointer",
                            }}
                          >{t("close")}</button>
                        )}
                        {tab === "history" && (
                          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ color: r.status === "closed" ? (r.pnl >= 0 ? "var(--iq-green)" : "#ef4444") : "var(--iq-text-dim)" }}>{r.status}</span>
                            {r.status === "closed" && (
                              <>
                                <button
                                  data-testid={`share-trade-${r.id}`}
                                  onClick={() => setShareTrade(r)}
                                  style={{ color: "var(--iq-text-dim)", background: "transparent", border: "none", cursor: "pointer", padding: "2px" }}
                                ><Share2 style={{ width: 12, height: 12 }} /></button>
                                <button
                                  data-testid={`note-trade-${r.id}`}
                                  onClick={() => editingNote === r.id ? saveNote(r.id) : startEditNote(r)}
                                  style={{ color: editingNote === r.id ? "var(--iq-green)" : "var(--iq-text-dim)", background: "transparent", border: "none", cursor: "pointer", padding: "2px" }}
                                >{editingNote === r.id ? <Check style={{ width: 12, height: 12 }} /> : <StickyNote style={{ width: 12, height: 12 }} />}</button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    {/* Note editor row */}
                    {tab === "history" && editingNote === r.id && (
                      <tr style={{ background: "var(--iq-bg)" }}>
                        <td colSpan={8} style={{ padding: "8px 12px" }}>
                          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <input
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              placeholder="Not ekle..."
                              style={{ flex: 1, background: "var(--iq-panel)", border: "1px solid var(--iq-border)", borderRadius: "4px", padding: "6px 8px", fontSize: "11px", color: "#fff", outline: "none", fontFamily: "'JetBrains Mono', monospace" }}
                              onKeyDown={(e) => e.key === "Enter" && saveNote(r.id)}
                            />
                            <input
                              value={noteTags}
                              onChange={(e) => setNoteTags(e.target.value)}
                              placeholder="Etiketler (virgülle)"
                              style={{ width: "140px", background: "var(--iq-panel)", border: "1px solid var(--iq-border)", borderRadius: "4px", padding: "6px 8px", fontSize: "11px", color: "#fff", outline: "none", fontFamily: "'JetBrains Mono', monospace" }}
                              onKeyDown={(e) => e.key === "Enter" && saveNote(r.id)}
                            />
                            <button onClick={() => saveNote(r.id)} style={{ padding: "6px 10px", background: "var(--iq-green)", color: "#000", border: "none", borderRadius: "4px", fontSize: "10px", fontWeight: 700, cursor: "pointer" }}>Kaydet</button>
                            <button onClick={() => setEditingNote(null)} style={{ padding: "6px", background: "transparent", border: "1px solid var(--iq-border)", borderRadius: "4px", cursor: "pointer", color: "var(--iq-text-dim)" }}><X style={{ width: 12, height: 12 }} /></button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <TradeShareModal
        trade={shareTrade}
        asset={shareTrade ? assetsMap?.[shareTrade.symbol] : null}
        open={!!shareTrade}
        onOpenChange={(v) => { if (!v) setShareTrade(null); }}
      />
    </div>
  );
}
